'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseCardProps } from '../cards/CourseCard.types';
import {
  AiHighlightsBlock,
  Breadcrumb,
  CareerBlock,
  ChapterTree,
  CourseLayout,
  CourseTabs,
  CourseTitleBlock,
  FaqAccordion,
  InstructorCard,
  OutcomesList,
  PriceCard,
  RatingHistogram,
  RelatedCourses,
  ReviewCard,
  SectionShell,
  SkillChips,
  StatRow,
  type CourseDetailProps,
  type TabSpec,
} from './shared';

// CourseDetailDecisionFirst — outcomes-led.
//
// "Should I commit to this?"
//
// The outcomes panel is the page's biggest argument and lives ABOVE
// the tabs (always visible — doesn't get tucked into a panel). The
// curriculum is in a tab, compacted (defaultOpen: false). The
// sidebar's price card is tall.
//
// Tab order optimized for "I'm thinking about buying":
//   curriculum → career → reviews → faq → instructor
//
// Curriculum is the first tab (since "what's in it" is still the
// natural follow-up to outcomes), but it's NOT pre-expanded.

interface DecisionFirstProps extends CourseDetailProps {
  SimilarCard: ComponentType<{ course: CourseCardProps }>;
}

export function CourseDetailDecisionFirst({
  tenantSlug,
  course,
  related,
  SimilarCard,
  enrollmentIsland,
}: DecisionFirstProps) {
  const { t } = useTranslation('common');
  const chapters = course.chapters ?? [];
  const firstLessonId = chapters[0]?.lessons?.[0]?.id ?? 'l1';

  const tabs: TabSpec[] = [
    { id: 'curriculum', label: t('label.content') },
    {
      id: 'career',
      label: t('label.skillsAndCareer'),
      show: Boolean(course.skills?.length || course.careerOutcomes),
    },
    { id: 'reviews', label: t('label.comments'), show: Boolean(course.reviewSummary) },
    { id: 'faq', label: t('label.FAQ'), show: (course.faqs?.length ?? 0) > 0 },
    { id: 'instructor', label: t('label.trainer') },
  ];

  return (
    <CourseLayout
      hero={
        <header className="cd-hero">
          <Breadcrumb
            tenantSlug={tenantSlug}
            categorySlug={course.category}
            categoryName={course.categoryName}
            courseTitle={course.title}
          />
          <CourseTitleBlock course={course} />
          <div style={{ marginTop: 12, marginBottom: 16 }}>
            <InstructorCard instructor={course.instructor} size="sm" />
          </div>
          <div style={{ marginBottom: 24 }}>
            <StatRow course={course} show={['rating', 'enrolled', 'duration', 'lessons']} />
          </div>

          {/* AI summary — small intro chip before the outcomes
              argument. Renders nothing if data is missing. */}
          {(course.aiHighlights?.length ?? 0) > 0 && (
            <div style={{ marginTop: 24, marginBottom: 16 }}>
              <AiHighlightsBlock highlights={course.aiHighlights} />
            </div>
          )}

          {/* THE outcomes panel — Decision-first's signature.
              Above the tabs, always visible, oversized. This is the
              hero argument. Now uses the shared `.cd-outcomes-hero-
              panel` class instead of inline styles so the styling
              stays in one place (styles.ts). */}
          {(course.outcomes?.length ?? 0) > 0 && (
            <div className="cd-outcomes-hero-panel" style={{ marginTop: 16 }}>
              <div className="cd-outcomes-hero-kicker">{t('label.byTheEndOfThisCourse')}</div>
              <OutcomesList outcomes={course.outcomes ?? []} size="hero" />
            </div>
          )}

          {/* Preview video lives in the PriceCard's preview thumbnail
              (Decision-first uses the 'premium' variant which features
              a larger preview with bigger play button). Rendering it
              here too would duplicate the same content. The PriceCard
              sits in the sidebar at the same grid row as the hero, so
              it's visually right next to the outcomes panel. */}
        </header>
      }
      main={
        <CourseTabs
          tabs={tabs}
          defaultTabId="curriculum"
          panels={{
            curriculum: (
              <SectionShell title={t('label.content')} subtitle={t('label.curriculumExpandHint')}>
                <ChapterTree
                  chapters={chapters}
                  defaultOpen={false}
                  compact
                  tenantSlug={tenantSlug}
                  courseId={course.id}
                />
              </SectionShell>
            ),
            career: (course.skills?.length || course.careerOutcomes) && (
              <SectionShell
                title={t('label.skillsAndCareer')}
                subtitle={t('label.careerOpportunitiesSubtitle')}
              >
                {course.skills && course.skills.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <SkillChips skills={course.skills} />
                  </div>
                )}
                {course.careerOutcomes && <CareerBlock career={course.careerOutcomes} />}
              </SectionShell>
            ),
            reviews: course.reviewSummary && (
              <SectionShell title={t('label.comments')}>
                <RatingHistogram
                  rating={course.ratingAverage}
                  ratingCount={course.ratingCount}
                  distribution={course.reviewSummary.distribution}
                />
                <div style={{ display: 'grid', gap: 12 }}>
                  {course.reviewSummary.featured.map((r, i) => (
                    <ReviewCard key={i} review={r} />
                  ))}
                </div>
              </SectionShell>
            ),
            faq: (course.faqs?.length ?? 0) > 0 && (
              <SectionShell title={t('title.faq')}>
                <FaqAccordion faqs={course.faqs ?? []} />
              </SectionShell>
            ),
            instructor: (
              <SectionShell title={t('label.trainer')}>
                <InstructorCard instructor={course.instructor} size="lg" />
              </SectionShell>
            ),
          }}
        />
      }
      sidebar={
        <PriceCard
          course={course}
          tenantSlug={tenantSlug}
          firstLessonId={firstLessonId}
          variant="premium"
          enrollAction={enrollmentIsland}
        />
      }
      full={
        related.length > 0 && (
          <SectionShell title={t('title.similarLearnings')}>
            <RelatedCourses courses={related} Card={SimilarCard} />
          </SectionShell>
        )
      }
    />
  );
}
